import React, { useState, useEffect } from "react";
import { addDays, format, parse } from "date-fns";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { CalendarIcon, CheckCircle } from "lucide-react";
import { CreateEnrollmentFormData, BankAccount, PensionConsent } from "@/types/pensions";
import { BankAccountForm } from "./BankAccountForm";
import { PensionSetupConsent } from "./PensionSetupConsent";
import PensionAPIService from "@/services/pension-api";

const formSchema = z
  .object({
    effectiveDate: z.date({
      required_error: "Effective date is required",
    }),
    coverageStartDate: z.date({
      required_error: "Coverage start date is required",
    }),
  })
  .refine(
    (data) => {
      return (
        !data.effectiveDate ||
        !data.coverageStartDate ||
        data.effectiveDate >= data.coverageStartDate
      );
    },
    {
      message: "Effective date cannot be before coverage start date",
      path: ["effectiveDate"],
    },
  );

interface CreateEnrollmentDialogProps {
  open: boolean;
  onClose: () => void;
  employeeId: number;
  employeeName: string;
  employeeStartDate?: Date | null;
  hasExistingEvent: boolean;
  onSubmit: (
    employeeId: number,
    effectiveDate: Date,
    coverageStartDate: Date,
  ) => Promise<void>;
}

enum EnrollmentStep {
  BANK_ACCOUNT = 0,
  CONSENT = 1,
  ENROLLMENT_DETAILS = 2,
}

const formatDateToDisplay = (date: Date): string => {
  return format(date, "dd/MM/yyyy");
};

const getFirstDayOfMonth = (date: Date | null | undefined): Date => {
  if (!date) {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  }
  return new Date(date.toISOString().split("T")[0] + "T00:00:00");
};

export default function CreateEnrollmentDialog({
  open,
  onClose,
  employeeId,
  employeeName,
  employeeStartDate,
  hasExistingEvent,
  onSubmit,
}: CreateEnrollmentDialogProps) {
  const [currentStep, setCurrentStep] = useState<EnrollmentStep>(EnrollmentStep.BANK_ACCOUNT);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bankAccount, setBankAccount] = useState<BankAccount | null>(null);
  const [pensionConsent, setPensionConsent] = useState<PensionConsent | null>(null);
  const [isBankAccountLoading, setIsBankAccountLoading] = useState(false);
  const [isConsentLoading, setIsConsentLoading] = useState(false);

  const form = useForm<CreateEnrollmentFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      effectiveDate: getFirstDayOfMonth(employeeStartDate),
      coverageStartDate: getFirstDayOfMonth(employeeStartDate),
    },
  });

  useEffect(() => {
    if (open) {
      // Reset to first step when dialog opens
      setCurrentStep(EnrollmentStep.BANK_ACCOUNT);
      form.reset({
        effectiveDate: getFirstDayOfMonth(employeeStartDate),
        coverageStartDate: getFirstDayOfMonth(employeeStartDate),
      });
      setError(null);
      setBankAccount(null);
      setPensionConsent(null);
    }
  }, [open, employeeStartDate, form]);

  const handleBankAccountSubmit = async (data: BankAccount) => {
    setIsBankAccountLoading(true);
    setError(null);
    
    try {
      const savedBankAccount = await PensionAPIService.saveBankAccount(data);
      setBankAccount(savedBankAccount);
      setCurrentStep(EnrollmentStep.CONSENT);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save bank account");
    } finally {
      setIsBankAccountLoading(false);
    }
  };

  const handleConsentSubmit = async (consent: PensionConsent) => {
    setIsConsentLoading(true);
    setError(null);
    
    try {
      const savedConsent = await PensionAPIService.recordConsent(consent);
      setPensionConsent(savedConsent);
      setCurrentStep(EnrollmentStep.ENROLLMENT_DETAILS);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to record consent");
    } finally {
      setIsConsentLoading(false);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > EnrollmentStep.BANK_ACCOUNT) {
      setCurrentStep(currentStep - 1);
      setError(null);
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case EnrollmentStep.BANK_ACCOUNT:
        return "Bank Account Details";
      case EnrollmentStep.CONSENT:
        return "Pension Setup Consent";
      case EnrollmentStep.ENROLLMENT_DETAILS:
        return "Enrollment Details";
      default:
        return "Pension Setup";
    }
  };

  const handleSubmit = async (values: CreateEnrollmentFormData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit(
        employeeId,
        values.effectiveDate,
        values.coverageStartDate,
      );
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to create enrollment";
      
      // Check if the error is due to payrun validation
      if (errorMessage.includes("Invalid enrollment window") && errorMessage.includes("Earliest valid date is")) {
        // Extract the date from the error message
        const dateMatch = errorMessage.match(/Earliest valid date is (\d{2}\/\d{2}\/\d{4})/);
        if (dateMatch && dateMatch[1]) {
          // Parse the British date format (DD/MM/YYYY)
          const [day, month, year] = dateMatch[1].split('/');
          const earliestDate = new Date(`${year}-${month}-${day}`);
          
          // Update form with the earliest valid date and show a helpful message
          form.setValue("effectiveDate", earliestDate);
          form.setValue("coverageStartDate", earliestDate);
          
          setError(
            `Due to approved payruns, the enrollment can only start from ${dateMatch[1]}. The dates have been updated automatically. Please click "Create Event" again to proceed.`
          );
          return;
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const enrollmentEndDate = form.watch("effectiveDate")
    ? addDays(form.watch("effectiveDate"), 30)
    : null;

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center space-x-2 mb-4">
      {[EnrollmentStep.BANK_ACCOUNT, EnrollmentStep.CONSENT, EnrollmentStep.ENROLLMENT_DETAILS].map((step, index) => (
        <div key={step} className="flex items-center">
          <div className={cn(
            "flex items-center justify-center w-7 h-7 rounded-full text-xs font-medium",
            currentStep === step ? "bg-blue-600 text-white" :
            currentStep > step ? "bg-green-600 text-white" :
            "bg-gray-200 text-gray-600"
          )}>
            {currentStep > step ? <CheckCircle className="w-3 h-3" /> : step + 1}
          </div>
          {index < 2 && (
            <div className={cn(
              "w-8 h-0.5 mx-1",
              currentStep > step ? "bg-green-600" : "bg-gray-200"
            )} />
          )}
        </div>
      ))}
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case EnrollmentStep.BANK_ACCOUNT:
        return (
          <BankAccountForm
            initialData={bankAccount || undefined}
            onSubmit={handleBankAccountSubmit}
            isLoading={isBankAccountLoading}
          />
        );
      
      case EnrollmentStep.CONSENT:
        return (
          <PensionSetupConsent
            initialConsent={pensionConsent || undefined}
            termsUrl={PensionAPIService.getPensionTermsPdfUrl()}
            onConsentSubmit={handleConsentSubmit}
            isLoading={isConsentLoading}
          />
        );
      
      case EnrollmentStep.ENROLLMENT_DETAILS:
        return (
          <div className="space-y-6">
            <Alert>
              <AlertDescription>
                Creating an enrollment event for {employeeName}. The enrollment
                window will remain open for 30 days from the effective date.
              </AlertDescription>
            </Alert>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(handleSubmit)}
                className="space-y-6"
              >
                <FormField
                  control={form.control}
                  name="effectiveDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Enrollment Window Start Date</FormLabel>
                      <FormControl>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal",
                                !field.value && "text-muted-foreground",
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {field.value ? (
                                formatDateToDisplay(field.value)
                              ) : (
                                <span>Pick a date</span>
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 pointer-events-auto">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              defaultMonth={
                                field.value ? new Date(field.value) : undefined
                              }
                              onSelect={field.onChange}
                              disabled={(date) =>
                                employeeStartDate ? date < employeeStartDate : false
                              }
                              classNames={{
                                nav_button: "h-7 w-7 bg-transparent p-0 text-gray-700 hover:text-gray-900",
                                caption_label: "text-sm font-medium text-gray-900",
                                day_outside: "day-outside text-gray-600 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
                                day_disabled: "text-gray-400 opacity-50 cursor-not-allowed",
                                day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 text-gray-900 hover:bg-accent hover:text-accent-foreground"
                              }}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </FormControl>
                      <FormDescription>
                        Enrollment window:{" "}
                        {field.value ? formatDateToDisplay(field.value) : "Not set"}{" "}
                        -{" "}
                        {enrollmentEndDate
                          ? formatDateToDisplay(enrollmentEndDate)
                          : "N/A"}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {!hasExistingEvent && (
                  <FormField
                    control={form.control}
                    name="coverageStartDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Coverage Start Date</FormLabel>
                        <FormControl>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className={cn(
                                  "w-full justify-start text-left font-normal",
                                  !field.value && "text-muted-foreground",
                                )}
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {field.value ? (
                                  formatDateToDisplay(field.value)
                                ) : (
                                  <span>Pick a date</span>
                                )}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 pointer-events-auto">
                              <Calendar
                                mode="single"
                                selected={field.value}
                                defaultMonth={
                                  field.value ? new Date(field.value) : undefined
                                }
                                onSelect={field.onChange}
                                disabled={(date) =>
                                  employeeStartDate
                                    ? date < employeeStartDate
                                    : false
                                }
                                classNames={{
                                  nav_button: "h-7 w-7 bg-transparent p-0 text-gray-700 hover:text-gray-900",
                                  caption_label: "text-sm font-medium text-gray-900",
                                  day_outside: "day-outside text-gray-600 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
                                  day_disabled: "text-gray-400 opacity-50 cursor-not-allowed",
                                  day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 text-gray-900 hover:bg-accent hover:text-accent-foreground"
                                }}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </FormControl>
                        <FormDescription>
                          When benefits coverage will begin
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={handlePreviousStep}>
                    Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting || !form.formState.isValid}
                  >
                    {isSubmitting ? "Creating..." : "Create Event"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] md:max-w-[700px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Step {currentStep + 1} of 3: {getStepTitle()}
          </DialogTitle>
        </DialogHeader>

        {renderStepIndicator()}

        {renderStepContent()}

        {error && currentStep !== EnrollmentStep.ENROLLMENT_DETAILS && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {currentStep !== EnrollmentStep.ENROLLMENT_DETAILS && (
          <DialogFooter>
            {currentStep > EnrollmentStep.BANK_ACCOUNT && (
              <Button type="button" variant="outline" onClick={handlePreviousStep}>
                Back
              </Button>
            )}
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
